import { LayerProperty } from 'cadenza/map/layer-property';
import { uniqueId } from 'cadenza/utils/unique-id';

export const LAYER_EVENT_TASKS_ONGOING = 'tasksOngoing';
export const LAYER_EVENT_TASK_REGISTERED = 'taskRegistered';
export const LAYER_EVENT_TASK_COMPLETED = 'taskCompleted';
export const LAYER_EVENT_ALL_TASKS_COMPLETED = 'allTasksCompleted';
export const LAYER_EVENT_TASKS_CANCELED = 'tasksCanceled';

/**
 * Returns the LayerTasks instance associated with the given layer.
 * If there is no existing instance, one will be created and stored in the layer properties.
 * The LayerTasks instance can be used to query, add, remove, cancel or restart tasks related to this layer.
 * Once a LayerTask has been associated to a layer and is used to add or remove tasks,
 * relevant events will be dispatched by the layer itself.
 *
 * @param layer - The layer
 * @return {LayerTasks} A LayerTask instance associated with the layer
 */
export function getLayerTasks (layer) {
  let layerTasks = layer.get(LayerProperty.TASKS);
  if (!layerTasks) {
    layerTasks = new LayerTasks(layer);
    layer.set(LayerProperty.TASKS, layerTasks);
  }
  return layerTasks;
}

export class LayerTasks {

  _layer;
  _ongoingTasks = new Map();
  _restartableTasks = new Map();

  constructor (layer) {
    this._layer = layer;
  }

  /**
   * Register a task.
   *
   * @param {?Function} cancelCallback - callback to be called when the task is canceled
   * @param {?Function} restartCallback - callback to be called when the task is restarted
   * @return {string} The generated task ID, for later reference, e.g. to call completeTask
   */
  registerTask (cancelCallback = null, restartCallback = null) {
    // when registering a new task, we start anew and old restartable tasks are cleared:
    this._restartableTasks.clear();

    const taskId = uniqueId();
    this._ongoingTasks.set(taskId, { cancelCallback, restartCallback });
    this._notifyTaskAdded();
    return taskId;
  }

  /**
   * Signals that the task referenced by the given ID is completed.
   * The task will be removed.
   *
   * @param taskId - {string} The ID of the task
   * @return {boolean} true if a task with the given ID was actually registered and has been removed, otherwise false
   */
  completeTask (taskId) {
    if (this.hasTask(taskId)) {
      this._ongoingTasks.delete(taskId);
      this._layer.dispatchEvent(LAYER_EVENT_TASK_COMPLETED);
      if (this._ongoingTasks.size === 0) {
        this._layer.dispatchEvent(LAYER_EVENT_ALL_TASKS_COMPLETED);
      }
      return true;
    }
    return false;
  }

  completeAllTasks () {
    this.ongoingTasks.forEach((_value, taskId, _tasks) => this.completeTask(taskId));
  }

  /**
   * Cancel all ongoing tasks.
   * If present, the cancel callback of each task will be called.
   * The tasks with restart callback will be saved as restartable tasks.
   */
  cancelTasks () {
    this._ongoingTasks.forEach((task, taskId) => {
      if (task.cancelCallback) {
        task.cancelCallback();
      }

      if (!task.restartCallback) {
        this._ongoingTasks.delete(taskId);
      }
    });

    this._restartableTasks = this._ongoingTasks;
    this._ongoingTasks = new Map();
    this._layer.dispatchEvent(LAYER_EVENT_TASKS_CANCELED);
  }

  /**
   * Restart the tasks that have been previously canceled and that have a restart callback.
   * The tasks will be set as ongoing again and the restart callback will be called.
   */
  restartTasks () {
    const restartableTasks = new Map(this._restartableTasks);
    restartableTasks.forEach((task, taskId) => {
      this._ongoingTasks.set(taskId, task);
      this._restartableTasks.delete(taskId);
      task.restartCallback();
      this._notifyTaskAdded();
    });
  }

  /**
   * Check if there are ongoing tasks
   *
   * @return {boolean} true if there's at least 1 ongoing task.
   */
  hasOngoingTasks () {
    return this._ongoingTasks.size > 0;
  }

  /**
   * Check if there are restartable tasks
   *
   * @return {boolean} true if there's at least 1 task ready to be restarted.
   */
  hasRestartableTasks () {
    return this._restartableTasks.size > 0;
  }

  getTask (taskId) {
    return this._ongoingTasks.get(taskId);
  }

  hasTask (taskId) {
    return !!this.getTask(taskId);
  }

  get ongoingTasks () {
    return this._ongoingTasks;
  }

  _notifyTaskAdded () {
    this._layer.dispatchEvent(LAYER_EVENT_TASK_REGISTERED);
    if (this._ongoingTasks.size === 1) {
      this._layer.dispatchEvent(LAYER_EVENT_TASKS_ONGOING);
    }
  }

}
